import type { SentrySurface } from './types';

export function isBrowserExtensionError(
  errorMessage: string,
  filename?: string,
): boolean {
  if (
    filename &&
    (filename.startsWith('chrome-extension://') ||
      filename.startsWith('moz-extension://'))
  ) {
    return true;
  }
  const extensionPatterns = [
    'chrome-extension://',
    'moz-extension://',
    'safari-extension://',
    'Invalid call to runtime.sendMessage(). Tab not found.',
  ];
  return extensionPatterns.some((pattern) => errorMessage.includes(pattern));
}

export function isNetworkAbortError(errorMessage: string): boolean {
  const abortPatterns = [
    'aborted',
    'The user aborted a request',
    'NetworkError',
    'Failed to fetch',
    'Load failed',
  ];
  return abortPatterns.some((pattern) =>
    errorMessage.toLowerCase().includes(pattern.toLowerCase()),
  );
}

export function isChunkLoadError(errorMessage: string): boolean {
  return (
    errorMessage.includes('ChunkLoadError') ||
    errorMessage.includes('Loading chunk') ||
    errorMessage.includes('Loading CSS chunk')
  );
}

export function isClientDisconnectError(errorMessage: string): boolean {
  const disconnectPatterns = [
    'ECONNRESET',
    'EPIPE',
    'ECONNABORTED',
    'socket hang up',
    'client disconnected',
  ];
  return disconnectPatterns.some((p) =>
    errorMessage.toLowerCase().includes(p.toLowerCase()),
  );
}

export function isValidationError(code: string | undefined): boolean {
  return (
    code === 'validation_error' ||
    code === 'invalid_input' ||
    code === 'bad_request'
  );
}

export function isPermissionDeniedError(code: string | undefined): boolean {
  return (
    code === 'permission_denied' ||
    code === 'forbidden' ||
    code === 'unauthorized'
  );
}

export function isExpectedNotFoundError(code: string | undefined): boolean {
  return code === 'not_found' || code === 'resource_not_found';
}

export function isIdempotentJobCondition(errorMessage: string): boolean {
  const idempotentPatterns = [
    'already_processed',
    'duplicate_job',
    'stale_payload',
    'no_op',
  ];
  return idempotentPatterns.some((p) =>
    errorMessage.toLowerCase().includes(p.toLowerCase()),
  );
}

export function shouldDropFrontendError(
  errorMessage: string,
  surface: SentrySurface,
  opts?: { filename?: string; code?: string },
): boolean {
  if (isBrowserExtensionError(errorMessage, opts?.filename)) return true;
  if (isNetworkAbortError(errorMessage) && surface !== 'guests') return true;
  return false;
}

export function shouldDropBackendError(
  errorMessage: string,
  code?: string,
): boolean {
  if (isClientDisconnectError(errorMessage)) return true;
  if (isValidationError(code)) return true;
  if (isPermissionDeniedError(code)) return true;
  return false;
}

export function shouldDropWorkerError(errorMessage: string): boolean {
  if (isIdempotentJobCondition(errorMessage)) return true;
  return false;
}

export type HttpErrorClass =
  | 'validation'
  | 'auth'
  | 'permission'
  | 'not_found'
  | 'conflict'
  | 'rate_limit'
  | 'unexpected';

export function classifyHttpError(
  statusCode: number,
  code?: string,
): HttpErrorClass {
  if (statusCode === 400 || isValidationError(code)) return 'validation';
  if (statusCode === 401) return 'auth';
  if (statusCode === 403 || isPermissionDeniedError(code)) return 'permission';
  if (statusCode === 404) return 'not_found';
  if (statusCode === 409) return 'conflict';
  if (statusCode === 429) return 'rate_limit';
  return 'unexpected';
}
